
import { FilmInventory } from "@/types/inventory";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslation } from "@/hooks/useTranslation";

interface DeleteFilmButtonProps {
  film: FilmInventory;
  onDeleteComplete: () => void;
  trigger?: React.ReactNode;
}

export function DeleteFilmButton({ 
  film, 
  onDeleteComplete, 
  trigger 
}: DeleteFilmButtonProps) {
  const { t } = useTranslation();
  
  const handleDelete = async () => {
    try {
      // Make sure we have a valid ID
      if (!film.id) {
        toast.error(t('cannot_delete_film_missing_id'));
        return;
      }

      const { error } = await supabase
        .from('film_inventory')
        .delete()
        .eq('id', film.id);

      if (error) {
        console.error('Error deleting film:', error);
        throw error;
      }
      
      toast.success(t('film_removed_inventory'));
      onDeleteComplete();
    } catch (error: any) {
      console.error('Error:', error);
      toast.error(`${t('failed_delete_film')}: ${error.message}`);
    }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        {trigger || (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="hover:bg-red-100">
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('delete_film')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('delete_film')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('confirm_delete_film')} {film.brand} {film.film_name} {t('from_your_inventory')}?
            {t('action_cannot_undone')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
          <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
            {t('delete')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
